import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import BillingBackButton from "./BillingBackButton";
import prepaidIcon from "../../../assets/images/icons/billing/prepaid.svg";
import postpaidIcon from "../../../assets/images/icons/billing/postpaid.svg";
import { getCurrencyConfig, getPostpaidEligibility, getBillingProfile } from "../../../hook/useAdsApi";

// Reasons GET /billing/postpaid/eligibility can return - kept in sync with
// checkPostpaidEligibility in client-api's ad-billing.service.js.
const INELIGIBILITY_REASONS = {
  no_ad_account: "Create an ad account first to unlock postpaid billing.",
  account_verification_required: "Your account needs to be verified before using postpaid billing.",
  outstanding_payment_failure: "Resolve your outstanding payment before switching to postpaid.",
};

export default function AdBillingModel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isFirstTimeSetup = searchParams.get("mode") === "setup";
  const adAccount = location.state?.adAccount;

  // Starts unset rather than defaulting to "prepaid" - defaulting it meant the Prepaid card
  // flashed as selected for however long getBillingProfile() took to resolve, then jumped to
  // Postpaid once it landed for a seller who'd actually saved Postpaid. Neither card highlights
  // until profileLoaded is true, so there's nothing to flash.
  const [selectedModel, setSelectedModel] = useState(null);
  // Separate from selectedModel - this only ever reflects what's actually saved on the profile,
  // so clicking the other card to explore it doesn't make "Currently active" jump around before
  // anything is actually saved.
  const [currentBillingModel, setCurrentBillingModel] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [currencies, setCurrencies] = useState(["USD"]);
  const [currency, setCurrency] = useState("USD");
  const [eligibility, setEligibility] = useState({ eligible: true, reason: null });

  useEffect(() => {
    getCurrencyConfig()
      .then((res) => {
        if (res?.supported_currencies?.length) setCurrencies(res.supported_currencies);
        if (res?.currency) setCurrency(res.currency);
      })
      .catch(() => {});
    getPostpaidEligibility()
      .then((res) => setEligibility(res || { eligible: true, reason: null }))
      .catch(() => {});
    // Reflect the actually-saved model when this screen is reached via "Change" on the
    // dashboard - without this it always showed Prepaid pre-selected even for a seller who had
    // already saved Postpaid, regardless of what they'd actually configured.
    getBillingProfile()
      .then((res) => {
        if (res?.configured && res.billing_model) {
          setSelectedModel(res.billing_model);
          setCurrentBillingModel(res.billing_model);
        } else {
          setSelectedModel("prepaid");
        }
      })
      .catch(() => setSelectedModel("prepaid"))
      .finally(() => setProfileLoaded(true));
  }, []);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-4xl">
            <div className="mb-6 flex flex-wrap gap-3 items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isFirstTimeSetup ? "Set Up Billing" : "Billing & Payments"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isFirstTimeSetup
                    ? "Choose a billing model to start running campaigns."
                    : "Select the billing model that works best for your advertising needs."}
                </p>
                <div className="border-b border-gray-200 mt-2 w-full max-w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <button
                type="button"
                onClick={() => setSelectedModel("prepaid")}
                className={`text-left rounded-2xl border-2 p-5 transition ${
                  selectedModel === "prepaid"
                    ? "border-[#1DE78B] bg-gradient-to-br from-[#eafcf3] to-[#eafbff]"
                    : "border-[#095228] bg-white"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <img src={prepaidIcon} alt="" className="w-8 h-8" />
                    <h3 className="font-bold text-[#095228]">Prepaid Credits</h3>
                  </div>
                  {currentBillingModel === "prepaid" && (
                    <span className="text-xs bg-[#cdeef7] text-[#0b3d55] rounded-full px-3 py-1">
                      Currently active
                    </span>
                  )}
                </div>
                <ul className="text-sm text-gray-700 space-y-2">
                  {[
                    "Add Funds in advance",
                    "Full Control over your spend",
                    "No Automatic Charges",
                    "Ideal for small and medium advertisers",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-gray-100 text-[#39B54A] flex items-center justify-center text-[10px]">
                        ✓
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </button>

              <button
                type="button"
                // TEMP (Siddharth) - eligibility gate disabled while dev/testing both flows;
                // restore `disabled={!eligibility.eligible}` and gate this onClick on
                // eligibility.eligible again before launch.
                onClick={() => setSelectedModel("postpaid")}
                className={`text-left rounded-2xl border-2 p-5 transition ${
                  selectedModel === "postpaid"
                    ? "border-[#1DE78B] bg-gradient-to-br from-[#eafcf3] to-[#eafbff]"
                    : "border-[#095228] bg-white"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <img src={postpaidIcon} alt="" className="w-8 h-8" />
                    <h3 className="font-bold text-[#095228]">Post Paid Credits</h3>
                  </div>
                  {currentBillingModel === "postpaid" && (
                    <span className="text-xs bg-[#cdeef7] text-[#0b3d55] rounded-full px-3 py-1">
                      Currently active
                    </span>
                  )}
                </div>
                <ul className="text-sm text-gray-700 space-y-2">
                  {[
                    "Pay after reaching a threshold or monthly",
                    "Automatic billing",
                    "Ideal for high spenders",
                    "Requires Verification",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-gray-100 text-[#39B54A] flex items-center justify-center text-[10px]">
                        ✓
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                {!eligibility.eligible && (
                  <p className="text-xs text-red-500 mt-3">
                    {INELIGIBILITY_REASONS[eligibility.reason] || "Not eligible for postpaid billing right now."}
                  </p>
                )}
              </button>
            </div>

            <div className="mb-6 max-w-xs">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Billing Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Threshold amount, billing cycle, and payment method are configured on the
                dedicated setup screen this routes to (AdAddCredits.js / AdPostpaidSetup.js), not
                inline here - this screen is model selection only, matching Figma. */}

            <div className="flex justify-center">
              <button
                type="button"
                disabled={!profileLoaded}
                onClick={() =>
                  navigate(
                    selectedModel === "prepaid"
                      ? "/ads/billing/add-credits"
                      : "/ads/billing/postpaid-setup",
                    { state: { currency, adAccount } }
                  )
                }
                className="bg-[#095228] text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
              >
                {profileLoaded ? "Continue →" : "Loading..."}
              </button>
            </div>
          </div>
        </main>
      </main>
    </div>
  );
}
